/**
 * Base class for all animations.
 *
 * This class provides the fundamental animation interface with a fraction-based approach.
 * The fraction (clamped to [0, 1]) represents the progress through the animation:
 * - 0.0 = animation start
 * - 1.0 = animation end
 *
 * Subclasses must implement the update() method to respond to fraction changes.
 *
 * This is a TypeScript port of the C++ Animation class from tabanim/public/animation/Animation.h
 *
 */

import { IAnimationUserData } from './IAnimationUserData';

export abstract class Animation {
  /**
   * The current fraction of the animation, clamped to [0, 1].
   * This is the fundamental driver of all animations.
   */
  private fraction = 0;

  /**
   * Optional user data that can be attached to this animation.
   * Useful for tracking, debugging, or storing custom metadata.
   */
  private userData?: IAnimationUserData;

  constructor() {
    this.fraction = 0;
  }

  /**
   * Sets the animation fraction, clamped to [0, 1].
   * Setting the fraction causes update() to be called.
   *
   * This method is NOT virtual - subclasses should override update()
   * to respond to fraction changes, not this method.
   *
   * @param fraction - The new fraction value (will be clamped to [0, 1])
   */
  public setFraction(fraction: number): void {
    // Clamp fraction to [0, 1] - same logic as C++:
    // m_fraction = (fraction < 0) ? 0 : ((fraction > 1) ? 1 : fraction);
    this.fraction = fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;

    // Call update() to allow subclasses to respond to the fraction change
    this.update();
  }

  public getFraction(): number {
    return this.fraction;
  }

  /**
   * Abstract method to be implemented by subclasses.
   * This method is called whenever setFraction() is called.
   *
   * Subclasses should override this method to update their internal state
   * based on the current fraction value.
   *
   */
  public abstract update(): void;

  public getUserData(): IAnimationUserData | undefined {
    return this.userData;
  }

  public setUserData(data: IAnimationUserData): void {
    this.userData = data;
  }
}
